import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, finalize, shareReplay, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { LocalStorageService } from './localStorage.service';
import { CommonSocketService } from './common-socket.service';
import { Event as PusherEvent } from 'src/app/pusher-event';

interface RestaurantDetailsCacheEntry {
  fetchedAt: number | null;
  response: any;
  inFlight?: Observable<any>;
}

@Injectable({
  providedIn: 'root',
})
export class RestaurantDetailsService {
  private readonly cacheTtlMs = 5 * 60 * 1000;
  private readonly cache = new Map<string, RestaurantDetailsCacheEntry>();
  private readonly details$ = new BehaviorSubject<any>(null);

  /** Stream of the latest `res.data` from restaurant details API. */
  readonly restaurantDetails$ = this.details$.asObservable();

  constructor(
    private _apiService: ApiService,
    private _localStorage: LocalStorageService,
    private _commonSocket: CommonSocketService
  ) {
    this.setupCacheInvalidation();
  }

  /** @deprecated Prefer `restaurantDetails$`; kept for callers using the BehaviorSubject directly. */
  get detailsSubject(): BehaviorSubject<any> {
    return this.details$;
  }

  getCurrentDetails(): any {
    return this.details$.value;
  }

  setDetails(data: any): void {
    this.details$.next(data);
  }

  getRestaurantDetails(
    param: FormData | { restaurant_id: string },
    forceRefresh = false
  ): Observable<any> {
    const restaurantId = this.extractRestaurantId(param);
    if (!restaurantId) {
      console.error('getRestaurantDetails called without a valid restaurant_id');
      return of(null);
    }

    const now = Date.now();
    const cached = this.cache.get(restaurantId);
    const isCacheFresh =
      !forceRefresh &&
      cached?.fetchedAt != null &&
      cached?.response != null &&
      now - cached.fetchedAt < this.cacheTtlMs;

    if (isCacheFresh) {
      return of(cached.response);
    }

    if (cached?.inFlight) {
      return cached.inFlight;
    }

    const request$ = this._apiService.fetchRestaurantDetails(param).pipe(
      tap((response) => {
        this.cache.set(restaurantId, {
          fetchedAt: Date.now(),
          response,
        });
        if (response?.status && response?.data) {
          this.setDetails(response.data);
        }
      }),
      shareReplay(1),
      finalize(() => {
        const entry = this.cache.get(restaurantId);
        if (entry?.inFlight === request$) {
          this.cache.set(restaurantId, {
            fetchedAt: entry.fetchedAt,
            response: entry.response,
          });
        }
      })
    );

    this.cache.set(restaurantId, {
      fetchedAt: cached?.fetchedAt ?? null,
      response: cached?.response ?? null,
      inFlight: request$,
    });

    return request$;
  }

  getRestaurantDetailsById(
    restaurantId: string,
    forceRefresh = false
  ): Observable<any> {
    const param = new FormData();
    param.append('restaurant_id', restaurantId);
    return this.getRestaurantDetails(param, forceRefresh);
  }

  invalidate(restaurantId?: string): void {
    if (restaurantId) {
      if (this.cache.has(restaurantId)) {
        this.cache.set(restaurantId, {
          fetchedAt: null,
          response: null,
        });
      }
      return;
    }

    this.cache.forEach((_, id) => {
      this.cache.set(id, {
        fetchedAt: null,
        response: null,
      });
    });
  }

  invalidateForCurrentRestaurant(): void {
    this.invalidate(this._localStorage.get('restaurant_id') || undefined);
  }

  private setupCacheInvalidation(): void {
    [
      PusherEvent.RESTAURANT_SETTINGS_EVENT,
      PusherEvent.RESTAURANT_DETAILS_EVENT,
    ].forEach((event) => {
      this._commonSocket.watchEvent(event).subscribe((data) => {
        const restaurantId =
          data?.restaurant_id ||
          data?.data?.restaurant_id ||
          this._localStorage.get('restaurant_id');
        this.invalidate(restaurantId || undefined);
      });
    });

    this._localStorage
      .watch('restaurant_id')
      .pipe(distinctUntilChanged())
      .subscribe((restaurantId) => {
        if (!restaurantId) {
          this.cache.clear();
          this.setDetails(null);
        }
      });
  }

  private extractRestaurantId(param: any): string | null {
    const restaurantId =
      param instanceof FormData ? param.get('restaurant_id') : param?.restaurant_id;
    if (!restaurantId || restaurantId === 'null' || restaurantId === 'undefined') {
      return null;
    }
    return String(restaurantId);
  }
}
